import {forwardRef, useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomBadge, {atomBadgeSizes, atomBadgeTypes} from '@s-ui/react-atom-badge'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import PrimitiveTypography, {
  primitiveTypographyDesign,
  primitiveTypographyFontSize,
  primitiveTypographyFontWeight
} from '@s-ui/react-primitive-typography'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import IconAngleRight from '@adv-mt/mt-svg-icons/lib/AngleRight'
import {AiIcon} from '@adv-mt/ui/ai-icon'
import {ICON_INTENTS, ICON_SIZES} from '@adv-mt/ui/icon'

const BASE_CLASS = 'mt-NetAccessBanner'

const NetAccessBanner = forwardRef(({className, onBlur, onClick}, ref) => {
  const {i18n, domain} = useContext(Context)
  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const isTablet = config.get('isTablet')
  const isDesktopOrWider = useMediaQuery(useMediaQueryBreakpoints.lg, {
    defaultMatches: !isMobile && !isTablet
  })
  const iconSize = isDesktopOrWider ? ICON_SIZES.MD : ICON_SIZES.SM

  return (
    <button
      ref={ref}
      aria-label={i18n.t('NET.ACCESS.ARIA_LABEL')}
      className={cx(BASE_CLASS, className)}
      onBlur={onBlur}
      onClick={onClick}
      onMouseDown={e => e.preventDefault()}
      type="button"
    >
      <span className={`${BASE_CLASS}-icon`} aria-hidden="true">
        <AiIcon intent={ICON_INTENTS.PRIMARY} size={iconSize} />
      </span>
      <span className={`${BASE_CLASS}-content`}>
        <span className={`${BASE_CLASS}-titleRow`}>
          <PrimitiveTypography
            as="span"
            className={`${BASE_CLASS}-title`}
            design={primitiveTypographyDesign.BODY_1}
            fontSize={primitiveTypographyFontSize.L}
            fontWeight={primitiveTypographyFontWeight.SEMI_BOLD}
          >
            {i18n.t('NET.ACCESS.TITLE')}
          </PrimitiveTypography>
          <span className={`${BASE_CLASS}-badgeMobile`}>
            <AtomBadge label={i18n.t('NET.ACCESS.BADGE')} size={atomBadgeSizes.SMALL} type={atomBadgeTypes.NEW} />
          </span>
        </span>
        <PrimitiveTypography
          as="span"
          className={`${BASE_CLASS}-description`}
          design={primitiveTypographyDesign.BODY_2}
          fontSize={primitiveTypographyFontSize.M}
        >
          {i18n.t('NET.ACCESS.DESCRIPTION')}
        </PrimitiveTypography>
      </span>
      <span className={`${BASE_CLASS}-badgeDesktop`}>
        <AtomBadge label={i18n.t('NET.ACCESS.BADGE')} size={atomBadgeSizes.LARGE} type={atomBadgeTypes.NEW} />
      </span>
      <span className={`${BASE_CLASS}-chevron`} aria-hidden="true">
        <IconAngleRight size={atomIconSizes.medium} />
      </span>
    </button>
  )
})

NetAccessBanner.displayName = 'NetAccessBanner'

NetAccessBanner.propTypes = {
  className: PropTypes.string,
  onBlur: PropTypes.func,
  onClick: PropTypes.func.isRequired
}

export default NetAccessBanner
